import api from '../mixins/api';
import moment from 'moment';
import { extendMoment } from 'moment-range';
extendMoment(moment);

export default class ProviderBookingManager {

    constructor(provider, services, userServices, users, workingTimes) {
        this.provider = provider;
        this.services = services;
        this.userServices = userServices;
        this.users = users;
        this.workingTimes = workingTimes;
        this.cachedUnavailableTimeSlots = [];
        this.memberWeekDayWorkingTimesMap = {};
        this.memberServiceWeekDayWorkingTimesMap = {};
        this.cachedServiceLeaves = {};// service_id => [leaves...]
        this.userMap = {};
        for (var i = 0 ; i < users.length ; i++) {
            this.userMap[users[i].id] = users[i];
        }
    }

    doesMemberProvideService(member, service) {
        return this.userServices.filter(item => item.user_id === member.id && item.service_id === service.id && !item.deleted_at ).length > 0;
    }

    saveBooking(booking, formPayload, callback) {
        const bookingPayload = {
            id: booking.id,
            service_id: booking.service.id,
            member_user_id: booking.member_user.id,
            date: moment(booking.start_datetime).format('YYYY-MM-DD'),
            start_time: moment(booking.start_datetime).format('HH:mm'),
            end_datetime: moment(booking.end_datetime).format('YYYY-MM-DD HH:mm'),
            parent_booking_id: booking.parent_booking_id,
            root_booking_id: booking.root_booking_id,
            note: booking.note
        };
        const customerPayload = {
            id: booking.customer.id,
            name: booking.customer.name,
            email: booking.customer.email,
            phone_number: booking.customer.phone_number,
            phone_calling_code: booking.customer.phone_calling_code,
        };

        const logisticPayload = booking.order?.logistic || null;
        let orderItemsPayload = [];

        if (booking.order && booking.order.items?.length) {
            orderItemsPayload = booking.order.items.map(item => {
                if (item.orderable_type === 'App\\Models\\ProviderItemSpec') {
                    return { spec_id: item.orderable_id, count: item.count }
                } else if (item.orderable_type === 'App\\Models\\ProviderItem') {
                    return { item_id: item.orderable_id, count: item.count }
                } else if (item.orderable_type === 'App\\Models\\ProviderCampaign') {
                    return { campaign_id: item.orderable_id }
                }
            })
        }

        api.methods.saveBooking(bookingPayload, customerPayload, formPayload, orderItemsPayload, logisticPayload, (response, error, errorKey) => {
            callback(response ? response.data : null, error, errorKey);
        });
    }

    isMemberAvailableForDateRange(member, service, start, end, exceptForBookingId, callback) {
        let reasons = [];
        const startDate = moment(start).tz(this.provider.timezone);
        const endDate = moment(end).tz(this.provider.timezone);
        const hasServiceSpecificWorkingTimes = !!this.getAllMemberServiceWorkingTimes(member, service);

        // ensure member wokrs at this time
        let withinExtraHours = false;
        let withinMemberWorksInDateRange = hasServiceSpecificWorkingTimes
            ? this.serviceMemberWorksInDateRange(member, service, startDate, endDate)
            : this.memberWorksInDateRange(member, startDate, endDate);
        const startFormatted = startDate.format('YYYY-MM-DD');
        const endFormatted = endDate.format('YYYY-MM-DD');
        api.methods.getMemberExtraHours(member.id, startFormatted, endFormatted, (response, error) => {
            const memberExtraHours = response.data || [];
            if (memberExtraHours.length > 0) {
                const validRanges = memberExtraHours.filter((item) => {
                    const startTime = moment(item.start_time).tz(this.provider.timezone);
                    const endTime = moment(item.end_time).tz(this.provider.timezone);
                    const rangeSelected = moment.range(startDate, endDate);
                    const rangeExtraHour = moment.range(startTime, endTime);
                    return rangeSelected.overlaps(rangeExtraHour, { adjacent: true });
                });
                withinExtraHours = validRanges.length > 0;
            }
            if (!withinMemberWorksInDateRange && !withinExtraHours) {
                reasons.push('NOT_WORKING_TIME');
            }
            api.methods.getUnavailableTimeSlotsForMember(member.id, start, end, (response, error) => {
                let hasData = !!response.data;
                let dataEmpty = response.data.length === 0;
                let dataContainsExceptBookingIdOnly = _.filter(response.data, (item) => {
                    return item.id !== exceptForBookingId && item.type === 'booking';
                });
                let ok = hasData && (dataEmpty || dataContainsExceptBookingIdOnly)
                if (!ok) {
                    reasons.push('LEAVE_OR_BOOKED');
                }
                callback(
                    reasons.length === 0,
                    error,
                    reasons
                );
            });
        })
    }

    unavailableDatesForMemberWithinRange(member, start, end, callback) {
        start = moment(start).format('YYYY-MM-DD HH:mm:ss');
        end = moment(end).format('YYYY-MM-DD HH:mm:ss');
        api.methods.getUnavailableTimeSlotsForMember(member.id, start, end, (response, error) => {
            if (response.data) {
                this.cachedUnavailableTimeSlots = response.data;
                callback(response.data, error);
            }
        });
    }

    worksBeforeDateTime(member, workingTimes, end) {
        member = this.userMap[member.id];
        var endMinutesOfDay = end.hour() * 60 + end.minute();
        for(var i = 0 ; i < workingTimes.length ; i++) {
            if (workingTimes[i].endMinutesOfDay >= endMinutesOfDay) {
                return true;
            }
        }
        return false;
    }

    memberWorksBeforeDateTime(member, end) {
        const workingTimes = this.memberWorkingTimesAtWeekDay(member, end.isoWeekday());
        return this.worksBeforeDateTime(member, workingTimes, end);
    }

    serviceMemberWorksBeforeDateTime(member, service, end) {
        const workingTimes = this.memberServiceWorkingTimesAtWeekday(member, service, end.isoWeekday());
        return this.worksBeforeDateTime(member, workingTimes, end);
    }

    worksInDateRange(workingTimes, start, end) {
        var startMinutesOfDay = start.hour() * 60 + start.minute();
        var endMinutesOfDay = end.hour() * 60 + end.minute();
        if (end.hour() < start.hour()) {
            // it's crossing day
            return false;
        }

        if (start.date() != end.date()) {
            return false;
        }

        for(var i = 0 ; i < workingTimes.length ; i++) {
            if (workingTimes[i].startMinutesOfDay <= startMinutesOfDay && workingTimes[i].endMinutesOfDay >= endMinutesOfDay) {
                return true;
            }
        }

        return false;
    }

    memberWorksInDateRange(member, start, end) {
        member = this.userMap[member.id];
        const workingTimes = this.memberWorkingTimesAtWeekDay(member, start.isoWeekday());
        return this.worksInDateRange(workingTimes, start, end);
    }

    serviceMemberWorksInDateRange(member, service, start, end) {
        member = this.userMap[member.id];
        const workingTimes = this.memberServiceWorkingTimesAtWeekday(member, service, start.isoWeekday());
        return this.worksInDateRange(workingTimes, start, end);
    }

    memberWorkingTimesAtWeekDay(member, isoWeekday) {
        if (this.memberWeekDayWorkingTimesMap[member.id]) {
            return this.memberWeekDayWorkingTimesMap[member.id][isoWeekday] || [];
        }

        var cachedResult = {};
        const memberWorkingDays = this.memberWorkingTimes(member);
        for (var i = 0 ; i < memberWorkingDays.length ; i++) {
            let workingDay = memberWorkingDays[i];
            if (!cachedResult[workingDay.weekday]) {
                cachedResult[workingDay.weekday] = [];
            }

            let startComponents = workingDay.start_time.split(':');
            let endComponents = workingDay.end_time.split(':');
            cachedResult[workingDay.weekday].push({
                startMinutesOfDay: parseInt(startComponents[0], 10) * 60 + parseInt(startComponents[1]),
                endMinutesOfDay: parseInt(endComponents[0], 10) * 60 + parseInt(endComponents[1]),
            });
        }
        this.memberWeekDayWorkingTimesMap[member.id] = cachedResult;

        return this.memberWeekDayWorkingTimesMap[member.id][isoWeekday] || [];
    }

    getMemberServiceWorkingTimes(member, service) {
        if (!this.memberServiceWeekDayWorkingTimesMap[service.id]) {
            this.memberServiceWeekDayWorkingTimesMap[service.id] = {};
        }
        if (this.memberServiceWeekDayWorkingTimesMap[service.id][member.id]) {
            return this.memberServiceWeekDayWorkingTimesMap[service.id][member.id];
        }

        var cachedResult = {1: [], 2: [], 3:[], 4: [], 5: [], 6: [], 7:[]};
        const memberServiceWorkingTimes = service.member_working_times.filter((item) => item.member_user_id == member.id);
        var hasAny = false;
        if (memberServiceWorkingTimes.length > 0) {
            for (var i = 0; i < memberServiceWorkingTimes.length ; i++) {
                let workingDay = memberServiceWorkingTimes[i];
                if (!cachedResult[workingDay.weekday]) {
                    cachedResult[workingDay.weekday] = [];
                }

                let startComponents = workingDay.start_time.split(':');
                let endComponents = workingDay.end_time.split(':');
                cachedResult[workingDay.weekday].push({
                    ...workingDay,
                    startMinutesOfDay: parseInt(startComponents[0], 10) * 60 + parseInt(startComponents[1]),
                    endMinutesOfDay: parseInt(endComponents[0], 10) * 60 + parseInt(endComponents[1]),
                });
                hasAny = true;
            }
        }

        if (!hasAny) {
            cachedResult = null;
        }

        this.memberServiceWeekDayWorkingTimesMap[service.id][member.id] = cachedResult;
        return cachedResult;
    }

    getAllMemberServiceWorkingTimes(member, service) {
        const cachedResult = this.getMemberServiceWorkingTimes(member, service);
        if (cachedResult) {
            return Object.values(cachedResult).flatMap((item) => item);
        } else {
            return null;
        }
    }

    memberServiceWorkingTimesAtWeekday(member, service, isoWeekday) {
        const workingTimes = this.getMemberServiceWorkingTimes(member, service);
        if (workingTimes) {
            return workingTimes[isoWeekday] || [];
        }
        return null;
    }

    memberWorkingTimes(member) {
        const user = this.userMap[member.id];
        return user ? user.working_days : [];
    }

    getServiceLeaves(serviceId, cb) {
        if (this.cachedServiceLeaves[serviceId]) {
            cb(this.cachedServiceLeaves[serviceId]);
        } else {
            api.methods.getServiceLeaves(serviceId, (response, error) => {
                if (!error) {
                    this.cachedServiceLeaves[serviceId] = response.data;
                    cb(this.cachedServiceLeaves[serviceId]);
                }
            });
        }
    }

    getExistingCustomer(phoneCallingCode, phoneNumber, callback) {
        api.methods.checkExistingMember(phoneCallingCode, phoneNumber, (response) => {
            if (response && response.data) {
                callback(response.data)
            } else {
                callback(null)
            }
        });
    }

    // startMoment means the start time of the booking selected
    getAvailableDateTimeForServiceAndMember({provider, service, member, startDate, endDate, skipBookingId, ignoreServiceStartTime, startMoment, callback}) {
        const dateKeyFormat = 'YYYY-MM-DD';
        const start = moment(startDate).tz(provider.timezone, true).startOf('day');
        const end = moment(endDate).tz(provider.timezone, true).endOf('day');
        const startFormatted = start.format(dateKeyFormat);
        const endFormatted = end.format(dateKeyFormat);
        api.methods.getMemberExtraHours(member.id, startFormatted, endFormatted, (response, error) => {
            const memberExtraHours = response.data || [];
            api.methods.getServiceExtraHours(service.id, startFormatted, endFormatted, (response, error) => {
                const serviceExtraHours = response.data || [];
                this.getServiceLeaves(service.id, (serviceLeaves) => {
                    this.unavailableDatesForMemberWithinRange(member, startDate, endDate, (dates, error) => {
                        // Preprocess unavailable dates
                        var unavailableDates = dates;
                        var unavailableDatesMap = {};
                        var serviceCountOnDateMap = {};

                        for (var i in unavailableDates) {
                            let item = unavailableDates[i];
                            let momentStart = moment.tz(item.start, provider.timezone);
                            let momentEnd = moment.tz(item.end, provider.timezone);
                            let date = momentStart.format('YYYY-MM-DD');

                            if (item.type === 'booking') {
                                if (!serviceCountOnDateMap[date]) {
                                    serviceCountOnDateMap[date] = {};
                                }
                                if (!serviceCountOnDateMap[date][item.service_id]) {
                                    serviceCountOnDateMap[date][item.service_id] = 1;
                                } else {
                                    serviceCountOnDateMap[date][item.service_id] += 1;
                                }
                            }

                            if (item.type === 'booking') {
                                if (!unavailableDatesMap[date]) {
                                    unavailableDatesMap[date] = [];
                                }
                                unavailableDatesMap[date].push(item);
                            } else {
                                // Check if it's a cross day event like multiple day leaves
                                let s = momentStart.clone();
                                while (s.isBefore(momentEnd)) {
                                    var key = s.format('YYYY-MM-DD'); 
                                    if (!unavailableDatesMap[key]) {
                                        unavailableDatesMap[key] = [];
                                    }
                                    unavailableDatesMap[key].push(item);
                                    s.add(1, 'day').startOf('day');
                                }
                            }
                        }

                        try {
                        const dateSlotsMap = {};

                        const hasServiceSpecificWorkingTimes = !!this.getAllMemberServiceWorkingTimes(member, service);
                        const serviceLimitedPeriodRanges = service.limited_periods.map(p => moment.range(
                                moment.tz(p.start, 'GMT'),
                                moment.tz(p.end, 'GMT')
                            )
                        );

                        let currentDate = moment.tz(start, provider.timezone);
                        while (currentDate.isSameOrBefore(end)) {
                            const currentDateKey = currentDate.format(dateKeyFormat);
                            dateSlotsMap[currentDateKey] = [];
                            console.log(currentDateKey);

                            if (currentDate.isBefore(moment(), 'day')) {
                                currentDate.add(1, 'day');
                                console.log('skip', currentDateKey);
                                continue;
                            }

                            const currentDateRange = moment.range(
                                currentDate.clone().startOf('day'),
                                currentDate.clone().endOf('day')
                            );

                            // Get working time ranges for this date
                            let workingRanges = [];
                            const weekday = currentDate.isoWeekday();
                            const workingTimes = hasServiceSpecificWorkingTimes ? 
                                this.memberServiceWorkingTimesAtWeekday(member, service, weekday) :
                                this.memberWorkingTimesAtWeekDay(member, weekday);

                            // Add working time ranges for this date
                            workingTimes.forEach(time => {
                                const rangeStart = currentDate.clone()
                                    .hours(Math.floor(time.startMinutesOfDay / 60))
                                    .minutes(time.startMinutesOfDay % 60);
                                const rangeEnd = currentDate.clone()
                                    .hours(Math.floor(time.endMinutesOfDay / 60))
                                    .minutes(time.endMinutesOfDay % 60);
                                workingRanges.push(moment.range(rangeStart, rangeEnd));
                            });

                            // Add member extra hours to working ranges
                            memberExtraHours.forEach((item) => {
                                const startTime = moment.tz(item.start_time, 'GMT').tz(provider.timezone);
                                const endTime = moment.tz(item.end_time, 'GMT').tz(provider.timezone);

                                // Check if extra hour is within current date
                                if (startTime.isSame(currentDate, 'day')) {
                                    workingRanges.push(moment.range(startTime, endTime));
                                    console.log('extra hour', workingRanges);
                                }
                            });

                            // Merge adjacent ranges
                            console.log('before merge', workingRanges.map(range => range.toDate()));
                            workingRanges = this.mergeAdjecntRanges(workingRanges);
                            console.log('after merge', workingRanges.map(range => range.toDate()));

                            if (serviceLimitedPeriodRanges.length > 0) {
                                // 有限制期間的話，將 member working hour ranges 跟 service extra hours + limited period 做 intersection
                                const limitedPeriodRanges = this.mergeAdjecntRanges([
                                    ...serviceLimitedPeriodRanges.filter(range => range.overlaps(currentDateRange)),
                                    ...serviceExtraHours.map(extraHour => moment.range(
                                        moment.tz(extraHour.start_time, 'GMT'),
                                        moment.tz(extraHour.end_time, 'GMT')
                                    ))
                                    .filter(range => range.overlaps(currentDateRange))
                                ]);
                                const intersectionedRanges = [];
                                limitedPeriodRanges.forEach(range => {
                                    intersectionedRanges.push(...workingRanges.map(workingRange => workingRange.intersect(range)));
                                });
                                workingRanges = this.mergeAdjecntRanges(intersectionedRanges);
                            }

                            if (service.start_time) {
                                const components = service.start_time.split(':');
                                const startTimeDayMinutes = parseInt(components[0]) * 60 + parseInt(components[1]);
                                if (service.allow_custom_duration) {
                                    const startMoment = moment.tz(currentDateKey, provider.timezone).startOf('day').add(startTimeDayMinutes, 'minutes');
                                    workingRanges = workingRanges.filter(range => range.contains(startMoment));
                                } else {
                                    // 不能自訂結束時間，使用服務總長來計算
                                    const endTimeDayMinutes = startTimeDayMinutes + parseInt(service.duration_minutes);
                                    const fixedStartTimeSlotRange = moment.range(
                                        moment.tz(currentDateKey, provider.timezone).startOf('day').add(startTimeDayMinutes, 'minutes'),
                                        moment.tz(currentDateKey, provider.timezone).startOf('day').add(endTimeDayMinutes, 'minutes')
                                    );
                                    const hasContainingRange = workingRanges.some(range => range.contains(fixedStartTimeSlotRange));
                                    if (hasContainingRange) {
                                        workingRanges = [fixedStartTimeSlotRange];
                                    }
                                }
                            }

                            // Subtract service leaves
                            const subtractLeaveCallback = (leave) => {
                                const leaveRange = moment.range(moment.tz(leave.start_time, 'GMT'), moment.tz(leave.end_time, 'GMT'));
                                workingRanges = workingRanges
                                    .map(range => range.subtract(leaveRange))
                                    .flat()
                                    .filter(range => range);
                            };
                            serviceLeaves.forEach(leave => subtractLeaveCallback(leave));

                            workingRanges = workingRanges.filter(range => range);

                            // Generate time slots from remaining ranges
                            dateSlotsMap[currentDateKey] = this.generateTimeSlotsFromRanges(
                                workingRanges,
                                service,
                                ignoreServiceStartTime
                            );
                            console.log(dateSlotsMap[currentDateKey]);

                            currentDate.add(1, 'day');
                        }
                        console.log('date slot map', dateSlotsMap);

                        const now = moment();
                        const result = [];
                        while(start.toDate() <= end.toDate()) {
                            var weekday = start.weekday();
                            if (weekday === 0) {
                                weekday = 7;
                            }

                            let startDateKey = start.format('YYYY-MM-DD');
                            // Check is the service max amount is reached if so, just go to next date
                            if (service.max_amount_per_day_per_member >= 0) {
                                if (serviceCountOnDateMap[startDateKey]
                                    && serviceCountOnDateMap[startDateKey][service.id]
                                    && serviceCountOnDateMap[startDateKey][service.id] >= service.max_amount_per_day_per_member
                                ) {
                                    start.add(1, 'day');
                                    continue;
                                }
                            }
                            const times = dateSlotsMap[startDateKey];
                            for (var i = 0 ; i < times.length ; i++) {
                                var components = times[i].split(':');
                                var slotStart = start.clone().hour(components[0]).minute(components[1]).second(0);
                                var slotEnd = slotStart.clone().add(service.duration_minutes, 'minute');
                                const rangeSlot = moment.range(slotStart, slotEnd);

                                if (now.isAfter(slotStart)) {
                                    // slotStart is in the past, don't add this into selectable timeslots
                                    continue;
                                }

                                // Time should later than start accepting booking
                                if (service.check_accept_booking_from) {
                                    var acceptBookingFromDate = slotStart.clone().subtract(service.accept_booking_from_value, service.accept_booking_from_unit);
                                    if (service.accept_booking_from_unit !== 'hour' && service.accept_booking_from_unit !== 'minute') {
                                        acceptBookingFromDate = acceptBookingFromDate.startOf('day');
                                    }
                                    if (now.isBefore(acceptBookingFromDate)) {
                                        continue;
                                    }
                                }

                                // Time should before stop accepting booking
                                if (service.check_accept_booking_until) {
                                    var acceptBookingUntilDate = slotStart.clone().subtract(service.accept_booking_until_value, service.accept_booking_until_unit);
                                    if (service.accept_booking_until_unit !== 'hour' && service.accept_booking_until_unit !== 'minute') {
                                        if (service.accept_booking_until_day_time) {
                                            // Use the specific time from accept_booking_until_day_time
                                            const [hours, minutes, seconds] = service.accept_booking_until_day_time.split(':');
                                            acceptBookingUntilDate.hour(parseInt(hours));
                                            acceptBookingUntilDate.minute(parseInt(minutes));
                                            acceptBookingUntilDate.second(parseInt(seconds || 0));
                                        } else {
                                            acceptBookingUntilDate = acceptBookingUntilDate.endOf('day');
                                        }
                                    }
                                    if (now.isAfter(acceptBookingUntilDate)) {
                                        continue;
                                    }
                                }

                                var overlapped = false;
                                var unavailableDatesForThisDay = unavailableDatesMap[startDateKey];
                                // !ignoreServiceStartTime means it's calculating for start time
                                // so if ignoreServiceStartTime is true, it's calculating for end time so don't need to check overlappings
                                if (!ignoreServiceStartTime && unavailableDatesForThisDay) {
                                    for (var j = 0; j < unavailableDatesForThisDay.length ; j++) {
                                        const blocked = unavailableDatesForThisDay[j];
                                        const rangeBlocked = moment.range(
                                            blocked.start,
                                            blocked.end
                                        );
                                        
                                        if (rangeSlot.overlaps(rangeBlocked)) {
                                            // if it's a multiple booking allowed, set it as non-overlapped
                                            if (blocked.type === 'booking' && blocked.allow_multiple && service.multiple_booking_allowed) {
                                                overlapped = false;
                                            } else if (blocked.type === 'booking' && blocked.id === skipBookingId) {
                                                overlapped = false;
                                            } else {
                                                overlapped = true;
                                                break;
                                            }
                                        }
                                    }
                                }

                                if (!overlapped) {
                                    result.push(slotStart);
                                }
                            }

                            start.add(1, 'day');
                        }

                        console.log('result', result.map(item => item.toDate()));

                        this.availableDates = [];
                        this.availableTimes = {};
                        for (var i = 0 ; i < result.length ; i++) {
                            var date = result[i].format('YYYY-MM-DD');
                            var time = result[i].format('HH:mm');

                            if (this.availableDates.indexOf(date) === -1) {
                                this.availableDates.push(date);
                            }
                            if (!this.availableTimes[date]) {
                                this.availableTimes[date] = [];
                            }
                            this.availableTimes[date].push(time);
                        }
                        const final = {
                            dates: this.availableDates,
                            times: this.availableTimes
                        };
                        console.log('final', final);
                        callback(final, null);
                    }catch(e) {
                        console.log(e);
                    }
                    });
                });
            });
        });
    }

    // helpers

    mergeAdjecntRanges(ranges) {
        ranges = ranges.sort((a, b) => a.start - b.start);
        let i = 0;
        while(i < ranges.length - 1) {
            if (ranges[i].overlaps(ranges[i + 1], { adjacent: true })) {
                ranges[i] = ranges[i].add(ranges[i + 1], {adjacent: true});
                ranges.splice(i + 1, 1);
            } else {
                i++;
            }
        }
        return ranges;
    }

    generateTimeSlotsFromRanges(ranges, service, ignoreServiceStartTime) {
        if (ranges.length === 0) {
            return [];
        }

        let times = [];
        ranges.forEach(range => {
            console.log('range', [ranges, range.toDate()])
            const startTime = range.start.format('HH:mm');
            const endTime = range.end.format('HH:mm');

            var startTimeComponents = startTime.split(':');
            startTimeComponents[0] = parseInt(startTimeComponents[0]);
            startTimeComponents[1] = parseInt(startTimeComponents[1]);
            var endTimeComponents = endTime.split(':');
            endTimeComponents[0] = parseInt(endTimeComponents[0]);
            endTimeComponents[1] = parseInt(endTimeComponents[1]);
            const workingEndTimeMinutesOfDay = endTimeComponents[0] * 60 + endTimeComponents[1];

            const check = () => {
                const minutesOfDay = startTimeComponents[0] * 60 + startTimeComponents[1];
                if (ignoreServiceStartTime) {
                    return minutesOfDay <= workingEndTimeMinutesOfDay;
                } else {
                    return (minutesOfDay + service.duration_minutes) <= workingEndTimeMinutesOfDay;
                }
            };

            while (check()) {  
                const timeToPush = startTimeComponents[0] + ':' + String(startTimeComponents[1]).padStart(2, '0');
                const isRepeat = !!times.find(time => timeToPush == time);
                if (!isRepeat) {
                    times.push(timeToPush);
                }
                startTimeComponents[1] += service.time_split_minutes;
                while (startTimeComponents[1] >= 60) {
                    startTimeComponents[1] -= 60;
                    startTimeComponents[0] += 1;
                }
            }
        });
        return times.sort((a, b) => parseInt(a.split(':')[0] * 60 + a.split(':')[1]) - parseInt(b.split(':')[0] * 60 + b.split(':')[1]));
    }

    // This utilizes cached service leaves and unavailable slots as conditions to check
    isTimeslotCanBeEndTimeslot(service, startMoment, timeslotMoment) {
        const range = moment.range(startMoment, timeslotMoment);

        // 1. not allowed to have member/service leaves prior to the slot at the same day
        const hasInBetweenServiceLeaves = !!(this.cachedServiceLeaves[service.id] || [])
            .find((leave) => {
                const leaveRange = moment.range(leave.start_time, leave.end_time);
                if (leaveRange.overlaps(range)) {
                    console.debug('END TIMESLOT BLOCKER (SERVICE LEAVE)', leave);
                    return true;
                } else {
                    return false;
                }
            });
        if (hasInBetweenServiceLeaves) {
            return false;
        }

        // 2. not allowed to have overlapped bookings with non-overlapable bookings or the service itself is non-overlappable
        const hasInBetweenBlockers = !!this.cachedUnavailableTimeSlots
            .find((blocker) => {
                if (!blocker.allow_multiple || !service.multiple_booking_allowed) {
                    const blockerRange = moment.range(blocker.start, blocker.end);
                    if (range.overlaps(blockerRange)) {
                        console.debug("END TIMESLOT BLOCKER", blocker);
                        return true;
                    }
                    return false;
                }
            });
        return !hasInBetweenBlockers;
    }

    static generateTimeSlotNoLimitation() {
        const startTime = '00:00';
        const endTime = '24:00';

        var startTimeComponents = startTime.split(':');
        startTimeComponents[0] = parseInt(startTimeComponents[0]);
        startTimeComponents[1] = parseInt(startTimeComponents[1]);
        var endTimeComponents = endTime.split(':');
        endTimeComponents[0] = parseInt(endTimeComponents[0]);
        endTimeComponents[1] = parseInt(endTimeComponents[1]);

        var times = [];
        while ((startTimeComponents[0] * 60 + startTimeComponents[1]) <= endTimeComponents[0] * 60 + endTimeComponents[1]) {
            times.push(String(startTimeComponents[0]).padStart(2, '0') + ':' + String(startTimeComponents[1]).padStart(2, '0'));
            startTimeComponents[1] += 10;
            while (startTimeComponents[1] >= 60) {
                startTimeComponents[1] -= 60;
                startTimeComponents[0] += 1;
            }
        }
        return times;
    }
}